import { create } from 'zustand'
import { getAxiosInstance, axiosRequest } from '../network/axiosInstance'

// Define interfaces for document library types
export interface DocumentVersion {
  id: string
  documentId: string
  versionNumber: number
  markdown: string
  createdAt: string
  createdBy?: string
  changeNotes?: string
}

export interface Document {
  id: string
  documentKey: string
  title: string
  category: string
  purpose: string
  extraContext?: string
  userRoles: string[]
  producers: string[]
  currentMarkdown: string
  currentVersion: number
  createdAt: string
  updatedAt: string
  organizationId?: string
  tags: string[]
  versions: DocumentVersion[]
  changeNotes?: string // Added for update operations
  isKnowledgeSet: boolean // Flag to indicate if document is part of the knowledge set
  isPublic: boolean // Flag to indicate if document is publicly accessible
}

export interface DocumentLibraryState {
  documentsLoading: boolean
  documentsFetchSuccess: boolean
  documentsFetchError: boolean
  documents: Document[] | null
  documentsErrorMessage: string | null
  fetchDocuments: (organizationId?: string) => Promise<void>
}

// Create a Zustand store for fetching documents
export const useGetDocuments = create<DocumentLibraryState>((set) => ({
  documentsLoading: false,
  documentsFetchSuccess: false,
  documentsFetchError: false,
  documents: null,
  documentsErrorMessage: null,
  fetchDocuments: async (organizationId?: string) => {
    console.log('fetchDocuments called')
    set({
      documentsLoading: true,
      documentsFetchSuccess: false,
      documentsFetchError: false,
      documents: null,
      documentsErrorMessage: null,
    })
    try {
      const params = organizationId ? { organizationId } : undefined
      const res = await getAxiosInstance().get('/api/documents', { params })
      set({
        documentsLoading: false,
        documentsFetchSuccess: true,
        documents: res.data.data,
      })
    } catch (err) {
      console.error('Error fetching documents:', err)
      set({
        documentsLoading: false,
        documentsFetchError: true,
        documentsErrorMessage: (err as Error).message,
      })
    }
  },
}))

// Get all documents
export const useGetAllDocuments = axiosRequest({
  method: 'GET',
  url: '/api/documents',
})

// Search documents
export const useSearchDocuments = axiosRequest({
  method: 'GET',
  url: '/api/documents/search',
})

// Get document by ID
export const useGetDocumentById = axiosRequest({
  method: 'GET',
  url: '/api/documents/:id',
})

// Get document by key
export const useGetDocumentByKey = axiosRequest({
  method: 'GET',
  url: '/api/documents/key/:key',
})

// Create document
export const useCreateDocument = axiosRequest({
  method: 'POST',
  url: '/api/documents',
})

// Update document - Fixed URL to prevent duplication
export const useUpdateDocument = axiosRequest({
  method: 'PUT',
  url: '/api/documents/:id',
})

// Delete document - Fixed URL to prevent duplication
export const useDeleteDocument = axiosRequest({
  method: 'DELETE',
  url: '/api/documents/:id',
})

// Get document versions
export const useGetDocumentVersions = axiosRequest({
  method: 'GET',
  url: '/api/documents/:id/versions',
})

// Get specific document version
export const useGetDocumentVersion = axiosRequest({
  method: 'GET',
  url: '/api/documents/:id/versions/:versionNumber',
})
